import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { IoEyeOutline, IoFilterOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline } from 'react-icons/io5';
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import { usePendingRequests } from '../../context/PendingRequestContext';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatDate } from '../../utils/format';
import { toast } from 'react-toastify';

function ManagerLeaveRequests() {
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();
  const { updatePendingCount } = usePendingRequests();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rejectComment, setRejectComment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [apiLoading, setLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isReapproving, setIsReapproving] = useState(false);
  
  // Bulk action states
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectComment, setBulkRejectComment] = useState('');
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkRejecting, setIsBulkRejecting] = useState(false);
  
  const requestsPerPage = 6;
  const baseUrl = process.env.REACT_APP_API_URL;
  
  
  const fetchLeaveRequests = async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/leaves/recent?managerId=${currentUser?.id}`);
      const dataWithIds = response.data.map(req => ({
        id: req.leaveRequestId,
        employeeName: req.employeeName,
        startDate: req.leaveStartDate,
        endDate: req.leaveEndDate,
        startDateTime: req.startDateTime,
        endDateTime: req.endDateTime,
        type: req.leaveType,
        status: req.leaveStatus,
        reason: req.reason,
        duration:req.duration,
        approverComment: req.approverComment
      }));
      setLeaveRequests(dataWithIds);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (currentUser?.id) {
      fetchLeaveRequests();
    }
  }, [currentUser?.id]);

  const filteredRequests = leaveRequests.filter(request => 
    statusFilter === 'all' ? true : request.status.toLowerCase() === statusFilter.toLowerCase()
  );


  // Calculate paginated data
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * requestsPerPage,
    currentPage * requestsPerPage
  );


  const handleView = (request) => {
    setSelectedRequest(request);
    setShowViewModal(true);
  };

  // Add this function to fetch pending count
  const fetchPendingCount = async () => {
    try {
      const response = await api.get(
        `/api/leaves/pending-count?managerId=${currentUser?.id}`
      );
      updatePendingCount(response.data);
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  // Update handleApprove
  const handleApprove = async (requestId, isReapproval = false) => {
    if (!currentUser?.id) return;
    isReapproval ? setIsReapproving(true) : setIsApproving(true);
    try {
      await api.put(`/api/leaves/approve/${requestId}`, null, {
        params: { managerId: currentUser?.id }
      });
      await fetchPendingCount();
      await fetchLeaveRequests();
      setShowViewModal(false);
      toast.success(`Leave request ${isReapproval ? 'reapproved' : 'approved'} successfully`);
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error(`${isReapproval ? 'Reapproval' : 'Approval'} failed`);
    } finally {
      isReapproval ? setIsReapproving(false) : setIsApproving(false);
    }
  };

  // Update handleReject
  const handleReject = async (requestId) => {
    if (!currentUser?.id) return;
    if (!rejectComment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setIsRejecting(true);
    try {
      await api.put(`/api/leaves/reject/${requestId}`, null, {
        params: {
          managerId: currentUser?.id,
          comment: rejectComment
        }
      });
      await fetchPendingCount();
      await fetchLeaveRequests();
      setShowViewModal(false);
      setRejectComment('');
      toast.success('Leave request rejected successfully');
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Rejection failed');
    } finally {
      setIsRejecting(false);
    }
  };

  // Bulk action handlers
  const handleSelectRequest = (requestId) => {
    setSelectedRequests(prev => {
      if (prev.includes(requestId)) {
        return prev.filter(id => id !== requestId);
      } else {
        return [...prev, requestId];
      }
    });
  };

  const handleSelectAll = () => {
    const currentPageIds = paginatedRequests.map(req => req.id);
    const allSelected = currentPageIds.every(id => selectedRequests.includes(id));

    if (allSelected) {
      // Unselect all on current page
      setSelectedRequests(prev => prev.filter(id => !currentPageIds.includes(id)));
    } else {
      // Select all on current page (keep previous selections)
      setSelectedRequests(prev => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  const handleBulkApprove = async () => {
    if (!currentUser?.id || selectedRequests.length === 0) return;
    
    setIsBulkApproving(true);
    try {
      // Process approvals sequentially to avoid backend race conditions
      const results = [];
      for (const requestId of selectedRequests) {
        try {
          const res = await api.put(`/api/leaves/approve/${requestId}`, null, {
            params: { managerId: currentUser?.id }
          });
          results.push({ status: 'fulfilled', value: res });
        } catch (err) {
          results.push({ status: 'rejected', reason: err });
        }
      }

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        toast.success(`${successCount} request(s) approved successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} request(s) failed to approve`);
      }

      await fetchPendingCount();
      await fetchLeaveRequests();
      setSelectedRequests([]);
    } catch (error) {
      console.error('Bulk approve failed:', error);
      toast.error('Bulk approval failed');
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleBulkReject = async () => {
    if (!currentUser?.id || selectedRequests.length === 0 || !bulkRejectComment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsBulkRejecting(true);
    try {
      // Process rejections sequentially
      const results = await Promise.allSettled(
        selectedRequests.map(requestId =>
          api.put(`/api/leaves/reject/${requestId}`, null, {
            params: {
              managerId: currentUser?.id,
              comment: bulkRejectComment
            }
          })
        )
      );

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        toast.success(`${successCount} request(s) rejected successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} request(s) failed to reject`);
      }

      await fetchPendingCount();
      await fetchLeaveRequests();
      setSelectedRequests([]);
      setShowBulkRejectModal(false);
      setBulkRejectComment('');
    } catch (error) {
      console.error('Bulk reject failed:', error);
      toast.error('Bulk rejection failed');
    } finally {
      setIsBulkRejecting(false);
    }
  };

  // Add check for currentUser and loading
  useEffect(() => {
    if (!loading) {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      if (currentUser.role !== 'MANAGER') {
        navigate('/unauthorized');
        return;
      }
    }
  }, [currentUser, loading, navigate]);

  // Show loading state
  if (loading || !currentUser) {
    return (
      <div className="p-4 sm:p-6 w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-48 bg-slate-200 rounded animate-pulse"></div>
          <div className="h-10 w-32 bg-slate-200 rounded-lg animate-pulse"></div>
        </div>
        <div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="h-[400px] flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // Add this to check if any action is in progress
  const isActionInProgress = isApproving || isRejecting || isReapproving;
  const isBulkActionInProgress = isBulkApproving || isBulkRejecting;

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <motion.div 
        className="flex justify-between items-center mb-6 bg-slate-200 rounded-2xl p-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h2 className="text-xl xs:text-2xl font-bold">Team Leave Requests</h2>
        <div className="flex items-center gap-2 ">
          <IoFilterOutline className="text-slate-600" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSelectedRequests([]);
            }}
            className="bg-white border border-slate-200 text-slate-600 text-sm rounded-full focus:ring-blue-500 focus:border-blue-500 p-2.5"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </motion.div>

      {/* Bulk Action Bar */}
      {selectedRequests.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex justify-between items-center"
        >
          <div className="flex items-center gap-2">
            <span className="font-semibold text-blue-800">
              {selectedRequests.length} request(s) selected
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleBulkApprove}
              disabled={isBulkActionInProgress}
              className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 ${
                isBulkActionInProgress ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              {isBulkApproving ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Approving...</span>
                </>
              ) : (
                <>
                  <IoCheckmarkCircleOutline className="text-lg" />
                  <span>Approve All</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowBulkRejectModal(true)}
              disabled={isBulkActionInProgress}
              className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 ${
                isBulkActionInProgress ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              <IoCloseCircleOutline className="text-lg" />
              <span>Reject All</span>
            </button>
            <button
              onClick={() => setSelectedRequests([])}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
            >
              Clear
            </button>
          </div>
        </motion.div>
      )}

      {/* Bulk Reject Modal */}
      {showBulkRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Reject Multiple Requests</h3>
              <button
                onClick={() => {
                  setShowBulkRejectModal(false);
                  setBulkRejectComment('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                You are about to reject <strong>{selectedRequests.length}</strong> request(s). 
                Please provide a reason for rejection.
              </p>
              <textarea
                placeholder="Enter reason for rejection..."
                value={bulkRejectComment}
                onChange={(e) => setBulkRejectComment(e.target.value)}
                className="w-full p-3 border border-slate-200 rounded-lg text-sm"
                rows={4}
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowBulkRejectModal(false);
                    setBulkRejectComment('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                  disabled={isBulkRejecting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkReject}
                  disabled={isBulkRejecting || !bulkRejectComment.trim()}
                  className={`px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 ${
                    isBulkRejecting || !bulkRejectComment.trim() ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {isBulkRejecting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Rejecting...</span>
                    </>
                  ) : (
                    "Reject All"
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {showViewModal && selectedRequest && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (!isActionInProgress && e.target === e.currentTarget) {
              setShowViewModal(false);
            }
          }}
          style={{ pointerEvents: isActionInProgress ? 'none' : 'auto' }}
        >
          <motion.div 
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Leave Request Details</h3>
              <button
                onClick={() => !isActionInProgress && setShowViewModal(false)}
                disabled={isActionInProgress}
                className={`text-slate-400 hover:text-slate-600 ${
                  isActionInProgress ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">Employee Name</p>
                  <p className="font-medium text-slate-800">{selectedRequest.employeeName}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">Leave Type</p>
                  <p className="font-medium text-blue-600">{selectedRequest.type}</p>
                </div>
              </div>
              {/* Leave Duration Field */}
              <div className="space-y-2">
                <p className="text-sm text-slate-500">Leave Duration</p>
                <p className="font-medium text-slate-800">{selectedRequest.duration} {selectedRequest.duration === 1 || selectedRequest.duration === 0.5 ? "day" : "days"}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">Start Date</p>
                  <p className="font-medium text-slate-800">{selectedRequest.startDate} ({selectedRequest.startDateTime})</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">End Date</p>
                  <p className="font-medium text-slate-800">{selectedRequest.endDate} ({selectedRequest.endDateTime})</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-slate-500">Reason</p>
                <p className="font-medium text-slate-800">{selectedRequest.reason}</p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                {(selectedRequest.status === "PENDING" || selectedRequest.status === "APPROVED") && (
                  <>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <span>Note:</span>
                      <span className="italic">Comment is required only for rejection</span>
                    </div>
                    <textarea
                      placeholder="Enter reason for rejection..."
                      value={rejectComment}
                      onChange={(e) => setRejectComment(e.target.value)}
                      className="w-full p-3 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                    />
                  </>
                )}
                
                {selectedRequest.status === "REJECTED" && selectedRequest.approverComment && (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-500">Rejection Reason</p>
                    <p className="font-medium text-slate-800">{selectedRequest.approverComment}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                {selectedRequest.status === "PENDING" && (
                  <>
                    <button
                      onClick={() => handleApprove(selectedRequest.id)}
                      disabled={isApproving}
                      className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 transition-colors disabled:text-green-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isApproving ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Approving...</span>
                        </>
                      ) : (
                        "Approve"
                      )}
                    </button>
                    <button
                      onClick={() => handleReject(selectedRequest.id)}
                      disabled={isRejecting}
                      className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 transition-colors disabled:text-red-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isRejecting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Rejecting...</span>
                        </>
                      ) : (
                        "Reject"
                      )}
                    </button>
                  </>
                )}

                {selectedRequest.status === "APPROVED" && (
                  <>
                    <button
                      disabled
                      className="px-4 py-2 text-sm font-medium text-slate-400 cursor-not-allowed"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(selectedRequest.id)}
                      disabled={isRejecting}
                      className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 transition-colors disabled:text-red-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isRejecting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Rejecting...</span>
                        </>
                      ) : (
                        "Reject"
                      )}
                    </button>
                  </>
                )}

                {selectedRequest.status === "REJECTED" && (
                  <>
                    <button
                      onClick={() => handleApprove(selectedRequest.id, true)}
                      disabled={isReapproving}
                      className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 transition-colors disabled:text-green-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isReapproving ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Reapproving...</span>
                        </>
                      ) : (
                        "Reapprove"
                      )}
                    </button>
                    <button
                      disabled
                      className="px-4 py-2 text-sm font-medium text-slate-400 cursor-not-allowed"
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Desktop View */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">
                  <input
                    type="checkbox"
                    checked={
                      paginatedRequests.length > 0 &&
                      paginatedRequests.every(req => selectedRequests.includes(req.id))
                    }
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Sl. No</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Employee</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Start Date & Time</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">End Date & Time</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Type</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Status</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-slate-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : null}

              {!apiLoading && paginatedRequests.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-slate-500">
                    No leave requests found.
                  </td>
                </tr>
              ) : null}

              {paginatedRequests.map((request, index) => (
                <tr key={request.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedRequests.includes(request.id)}
                      onChange={() => handleSelectRequest(request.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {(currentPage - 1) * requestsPerPage + index + 1}
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-700">{request.employeeName}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{formatDate(request.startDate)} ({request.startDateTime})</td>
                  <td className="px-6 py-4 text-slate-600">{formatDate(request.endDate)} ({request.endDateTime})</td>
                  <td className="px-6 py-4 text-slate-600">{request.type}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      request.status === "APPROVED" 
                        ? "bg-green-100 text-green-800"
                        : request.status === "PENDING"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleView(request)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="View Details"
                      >
                        <IoEyeOutline className="text-xl" />
                      </motion.button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination moved inside the table div */}
          <div className={`${ paginatedRequests.length > 0 ? 'block' : 'hidden'} px-6 py-4 border-t border-slate-100`}>
            <Stack spacing={2} className="flex justify-end">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="rounded-full"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#475569",
                    borderColor: "#e2e8f0",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#475569 !important",
                    color: "#fff",
                  },
                }}
              />
            </Stack>
          </div>
        </div>
      </motion.div>

      {/* Mobile View */}
      <div className="sm:hidden space-y-4">
        {apiLoading ? (
          <div className="flex justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          </div>
        ) : paginatedRequests.length > 0 ? (
          paginatedRequests.map((request, index) => (
            <motion.div
              key={request.id}
              className="bg-white p-5 rounded-xl shadow-md border border-slate-100"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedRequests.includes(request.id)}
                        onChange={() => handleSelectRequest(request.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-slate-500">
                        #{(currentPage - 1) * requestsPerPage + index + 1}
                      </span>
                      <h3 className="font-semibold text-slate-800">{request.employeeName}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                        {request.type}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        request.status === "APPROVED" 
                          ? "bg-green-100 text-green-800"
                          : request.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {request.status}
                      </span>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleView(request)}
                    className="text-blue-600 hover:text-blue-800 transition-colors bg-blue-50 p-2 rounded-full"
                  >
                    <IoEyeOutline className="text-xl" />
                  </motion.button>
                </div>
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Start Date:</span>
                    <span className="text-slate-700">{request.startDate}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">End Date:</span>
                    <span className="text-slate-700">{request.endDate}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-8 text-slate-500">
            No leave requests found.
          </div>
        )}

        {/* Mobile Pagination */}
        <div className="sm:hidden mt-6 flex justify-center">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="medium"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#fff",
                },
              }}
            />
          </Stack>
        </div>
      </div>
    </div>
  );
}

export default ManagerLeaveRequests;